/* eslint-disable @typescript-eslint/no-explicit-any -- Swagger records don't have a type */
import 'swagger-ui-react/swagger-ui.css';
import React, { FunctionComponent, useEffect, useState, useRef } from 'react';
import $RefParser, { JSONSchema } from '@apidevtools/json-schema-ref-parser';
import { useTranslation } from '@rbx/intl';
import { getTitleForLegacyCloudPage } from '@rbx/docsite-utilities/dist/browser';
import { TTranslateFunction } from '@modules/localization/types/TranslateFunction';
import SwaggerDocsContent from '@modules/openCloud/swagger/components/SwaggerDocsContent';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import { getHrefForViewAllLink } from '@modules/openCloud/swagger/utils/linkUtils';
import { getSwaggerDocsDescription } from '@modules/openCloud/swagger/utils/swaggerDocsDescription';
import throwError from '@modules/utilities/error';

type Endpoint = {
  path: string;
  method: string;
  scopesOverride?: string[];
};

type SwaggerDocsProps = {
  endpoints: Endpoint[] | 'all';
  matchers: string[];
  server: string;
  serverOverride?: string;
  showViewAllLink?: boolean;
  expandModels?: boolean;
  spec: Record<string, any>;
};

const useDereferencedSpec = (spec: Record<string, any>) => {
  const [dereferencedSpec, setDereferencedSpec] = useState<JSONSchema | Record<string, any>>(spec);
  const cache = useRef(new Map()); // Cache to store already dereferenced specs

  useEffect(() => {
    if (cache.current.has(spec)) {
      // Reuse cached result if available
      setDereferencedSpec(cache.current.get(spec));
      return;
    }
    let isMounted = true;
    const parseSchema = async () => {
      const dereferenced = await $RefParser.dereference(spec, {
        mutateInputSchema: false,
      });
      if (isMounted) {
        cache.current.set(spec, dereferenced); // Store result in cache
        setDereferencedSpec(dereferenced);
      }
    };
    parseSchema();
    // eslint-disable-next-line consistent-return
    return () => {
      isMounted = false; // Prevents setting state if component unmounts
    };
  }, [spec]);
  return dereferencedSpec;
};

const filterPaths = ({
  spec,
  dereferencedSpec,
  endpoints,
  matchers,
  translate,
  title,
}: {
  spec: Record<string, any>;
  dereferencedSpec: Record<string, any>;
  endpoints: SwaggerDocsProps['endpoints'];
  matchers: string[];
  translate: TTranslateFunction;
  title: string;
}): Record<string, Record<string, any>> => {
  let pathsToUse: Endpoint[] = [];
  const allPaths = Object.keys(spec.paths).flatMap((path) =>
    Object.keys(spec.paths[path]).map((method) => ({ path, method, scopesOverride: [] })),
  );

  const showSpecifiedEndpoints = endpoints !== 'all' && Array.isArray(endpoints);
  pathsToUse = showSpecifiedEndpoints ? endpoints : allPaths;
  if (endpoints !== 'all' && matchers && matchers.length > 0) {
    const matchedPaths = allPaths.filter(({ path }) =>
      matchers.some((matcher) => path.includes(matcher)),
    );
    if (showSpecifiedEndpoints) {
      // Combine matched paths with specified endpoints
      pathsToUse = [...new Set([...matchedPaths, ...pathsToUse])];
    } else {
      pathsToUse = matchedPaths;
    }
  }

  return pathsToUse.reduce<Record<string, Record<string, any>>>((acc, endpoint) => {
    if (!spec.paths[endpoint.path]) {
      throwError(`Path ${endpoint.path} is not found in spec for ${title}`);
      return acc;
    }
    if (!spec.paths[endpoint.path][endpoint.method]) {
      throwError(
        `Endpoint ${endpoint.method.toUpperCase()} ${
          endpoint.path
        } is not found in spec for ${title}`,
      );
      return acc;
    }
    // use the dereferenced spec only for the description
    const operation = spec.paths[endpoint.path][endpoint.method];
    const deRefedOp = dereferencedSpec.paths[endpoint.path][endpoint.method];
    if (dereferencedSpec.paths[endpoint.path] && operation) {
      // if the operation has a scopeOverride, we add the x-roblox-scopes scopes extension.
      // This is a temporary workaround for API Sites not yet onboarded with Public Web APIs.
      if (endpoint.scopesOverride && endpoint.scopesOverride.length > 0) {
        deRefedOp['x-roblox-scopes'] = endpoint.scopesOverride.map((scope) => ({ name: scope }));
      }

      if (!acc[endpoint.path]) {
        acc[endpoint.path] = {};
      }
      acc[endpoint.path][endpoint.method] = {
        ...operation,
        description: getSwaggerDocsDescription(translate, deRefedOp),
        tags:
          operation?.tags[0] === 'Cloud'
            ? [operation['x-roblox-docs']?.resource?.name || 'Cloud']
            : [], // Omit tags from legacy Cloud APIs
      };
    } else {
      const errorMessage = `Endpoint ${endpoint.method.toUpperCase()} ${
        endpoint.path
      } not found in spec for ${title}. Available paths:\n ${Object.keys(spec.paths).join('\n ')}`;
      throwError(errorMessage);
    }
    return acc;
  }, {});
};

const SwaggerDocs: FunctionComponent<React.PropsWithChildren<SwaggerDocsProps>> = ({
  endpoints,
  matchers,
  showViewAllLink = true,
  server,
  serverOverride,
  expandModels = false,
  spec,
}) => {
  const { translate } = useTranslation();
  const dereferencedSpec = useDereferencedSpec(spec) as Record<string, any>;

  if (!spec) {
    const errorMessage = 'Spec is undefined';
    throwError(errorMessage);
  }

  const title = spec.info?.title;
  if (!spec.paths) {
    const errorMessage = `Paths is not found in spec for ${title}`;
    throwError(errorMessage);
  }

  if (spec.servers || spec.servers?.length > 0) {
    const serverWithoutHttps = spec.servers?.[0]?.url.replace(/^https?:\/\//, '');
    if (server !== serverWithoutHttps && server !== spec.host) {
      const errorMessage = `Server "${server}" not found in spec for ${title}. Expected: ${
        serverWithoutHttps || (spec.host ? spec.host : '')
      }`;
      throwError(errorMessage);
    }
  }

  const paths = filterPaths({ spec, dereferencedSpec, endpoints, matchers, translate, title });
  const isSwagger2 = spec.swagger === '2.0';
  const isOpenCloudV2 = spec.openapi === '3.0.0' && spec.info?.title === 'Cloud API';

  const filteredSpec: Record<string, any> & { servers?: { url: string }[] } = {
    ...spec,
    paths,
    info: {
      ...spec.info,
      description:
        // filter out the description if the title is 'Cloud API'
        // because it's not helpful
        isOpenCloudV2 ? '' : spec.info?.description,
    },
    ...(isSwagger2 && serverOverride ? { host: serverOverride } : {}),
  };
  const serverOrHost = filteredSpec.host || filteredSpec.servers?.[0]?.url;
  const hrefForViewAllLink = getHrefForViewAllLink(serverOrHost, filteredSpec.info.version);

  return (
    <React.Fragment>
      <SwaggerDocsContent
        spec={JSON.stringify(filteredSpec)}
        defaultModelExpandDepth={4}
        defaultModelsExpandDepth={expandModels ? 4 : 0}
      />
      {showViewAllLink && endpoints !== 'all' && !isOpenCloudV2 && hrefForViewAllLink && (
        <DynamicallyRouted.Link href={hrefForViewAllLink}>
          {translate('Label.ViewAllEndpointsForName', {
            name: getTitleForLegacyCloudPage(spec.info.title),
          })}
        </DynamicallyRouted.Link>
      )}
    </React.Fragment>
  );
};

export default SwaggerDocs;
